import { Injectable } from '@angular/core'
import { UserModel } from '../../../core/models/user.model'
import { TechnicianTools } from '../../../core/tools/technician-tools'
import type { SlotIndicator } from './intervention-indicator.service'
import type { TechnicianWithDistance } from './availability-data.service'

/**
 * Auto-selection result
 */
export interface AutoSelectionResult {
  type: 'intervention' | 'home_technician' | 'selected_user' | 'none'
  intervention?: any
  technician?: UserModel
  preferredTab: 'slots' | 'emptyDays'
}

/**
 * Service responsible for technician selection logic and auto-selection algorithms
 */
@Injectable({
  providedIn: 'root',
})
export class TechnicianSelectionService {
  /**
   * Determine the best auto-selection based on available data
   * @param closestInterventions Available interventions sorted by proximity
   * @param interventionSlotIndicators Map of intervention ID to slot indicators
   * @param closestTechniciansByRoute Technicians sorted by home distance
   * @param users All available users
   * @param selectedUsers Currently selected users
   * @returns Auto-selection result with recommended action
   */
  determineAutoSelection(
    closestInterventions: any[],
    interventionSlotIndicators: Map<number, SlotIndicator>,
    closestTechniciansByRoute: TechnicianWithDistance[],
    users: UserModel[],
    selectedUsers: UserModel[]
  ): AutoSelectionResult {
    // Find the best intervention with feasible slots
    let bestIntervention: any = null
    let bestInterventionDurationMin: number | null = null

    if (closestInterventions && closestInterventions.length > 0) {
      // Find the closest intervention with feasible slots
      let closestFeasible: any = null
      let closestFeasibleDistance: number | null = null

      // Find the closest intervention with shift-slots (décaler)
      let closestShift: any = null
      let closestShiftDistance: number | null = null

      for (const intervention of closestInterventions) {
        const indicator = interventionSlotIndicators.get(intervention.id)
        const distance = intervention.distance ?? null

        if (indicator && indicator.feasibleCount > 0 && !closestFeasible) {
          closestFeasible = intervention
          closestFeasibleDistance = distance
        }

        if (indicator && indicator.shiftCount > 0 && !closestShift) {
          closestShift = intervention
          closestShiftDistance = distance
        }

        // Stop early if both found
        if (closestFeasible && closestShift) break
      }

      // Choose between feasible and shift based on distance:
      // If a shift-slot intervention is closer than the feasible one, prefer it
      if (closestFeasible && closestShift) {
        if (
          closestShiftDistance !== null &&
          closestFeasibleDistance !== null &&
          closestShiftDistance < closestFeasibleDistance
        ) {
          bestIntervention = closestShift
        } else {
          bestIntervention = closestFeasible
        }
      } else if (closestFeasible) {
        bestIntervention = closestFeasible
      } else if (closestShift) {
        bestIntervention = closestShift
      }

      if (bestIntervention) {
        bestInterventionDurationMin =
          bestIntervention.duration_min ?? bestIntervention.distance ?? null
      }

      // If no usable intervention found, take the first one as fallback
      if (!bestIntervention) {
        bestIntervention = closestInterventions[0]
        bestInterventionDurationMin =
          bestIntervention.duration_min ?? bestIntervention.distance ?? null
      }
    }

    // Check if a technician with a close home would be a better option
    // But only if the best intervention has NO usable slots (feasible or shift)
    const bestIndicator = bestIntervention
      ? interventionSlotIndicators.get(bestIntervention.id)
      : null
    const hasUsableSlots =
      bestIndicator &&
      (bestIndicator.feasibleCount > 0 || bestIndicator.shiftCount > 0)

    if (
      bestIntervention &&
      !hasUsableSlots &&
      closestTechniciansByRoute &&
      closestTechniciansByRoute.length > 0
    ) {
      const closestHomeTech = closestTechniciansByRoute[0]
      const homeDurationMin = closestHomeTech.duration_min
      const homeDistanceKm = closestHomeTech.distance_km

      // Compare: if the closest home is nearer than the best intervention,
      // prefer the home technician (they can open a new empty day)
      const shouldPreferHomeTechnician =
        bestInterventionDurationMin !== null &&
        homeDurationMin !== null &&
        homeDurationMin < bestInterventionDurationMin

      const shouldPreferHomeByDistance =
        !shouldPreferHomeTechnician &&
        homeDistanceKm !== null &&
        bestIntervention.distance !== null &&
        bestIntervention.distance !== undefined &&
        homeDistanceKm < bestIntervention.distance

      if (shouldPreferHomeTechnician || shouldPreferHomeByDistance) {
        // A technician lives closer — select them and show their empty days
        const closestTechnician = closestHomeTech.technician
        const userToSelect = users?.find(
          (user: UserModel) => user.id === closestTechnician.id
        )

        if (userToSelect) {
          return {
            type: 'home_technician',
            technician: userToSelect,
            preferredTab: 'emptyDays',
          }
        }
      }
    }

    // Default behavior: select the best intervention (if any)
    if (bestIntervention) {
      return {
        type: 'intervention',
        intervention: bestIntervention,
        preferredTab: 'slots',
      }
    }

    // No interventions at all: try selected users
    if (selectedUsers.length > 0) {
      return {
        type: 'selected_user',
        technician: selectedUsers[0],
        preferredTab: 'slots',
      }
    }

    // Fall back to closest home technician
    if (closestTechniciansByRoute && closestTechniciansByRoute.length > 0) {
      const closestTechnicianData = closestTechniciansByRoute[0]
      const userToSelect = users?.find(
        (user: UserModel) => user.id === closestTechnicianData.technician.id
      )

      if (userToSelect) {
        return {
          type: 'home_technician',
          technician: userToSelect,
          preferredTab: 'emptyDays',
        }
      }
    }

    return {
      type: 'none',
      preferredTab: 'slots',
    }
  }

  /**
   * Réordonne une liste de techniciens (déjà triée par proximité) en faisant
   * remonter en tête ceux rattachés à l'un des secteurs fournis (boost secteur),
   * tout en préservant l'ordre relatif d'origine au sein de chaque groupe.
   * Si aucun secteur ne correspond, la liste est renvoyée inchangée.
   * @param technicians Techniciens triés par distance routière
   * @param matchingSectorIds Ids des secteurs contenant le chantier
   * @returns Liste réordonnée (secteur d'abord, puis le reste)
   */
  prioritizeBySectors(
    technicians: TechnicianWithDistance[],
    matchingSectorIds: number[]
  ): TechnicianWithDistance[] {
    if (!matchingSectorIds?.length || !technicians?.length) {
      return technicians
    }

    const matchSet = new Set(matchingSectorIds)
    const inSector: TechnicianWithDistance[] = []
    const others: TechnicianWithDistance[] = []

    for (const item of technicians) {
      const isInSector = (item.technician?.sectors ?? []).some((sector) =>
        matchSet.has(sector.id)
      )
      ;(isInSector ? inSector : others).push(item)
    }

    return [...inSector, ...others]
  }

  /**
   * Find the closest technician by home distance
   * @param closestTechniciansByRoute Technicians sorted by home distance
   * @param users All available users
   * @returns The closest technician or null
   */
  findClosestTechnicianByHome(
    closestTechniciansByRoute: TechnicianWithDistance[],
    users: UserModel[]
  ): UserModel | null {
    if (!closestTechniciansByRoute || closestTechniciansByRoute.length === 0) {
      return null
    }

    const closestTechnicianData = closestTechniciansByRoute[0]
    return (
      users?.find(
        (user: UserModel) => user.id === closestTechnicianData.technician.id
      ) || null
    )
  }

  /**
   * Map intervention technicians to canonical user objects
   * @param interventionTechnicians Technicians from the intervention
   * @param users All available users
   * @returns Matched users
   */
  mapInterventionTechnicians(
    interventionTechnicians: any[],
    users: UserModel[]
  ): UserModel[] {
    return TechnicianTools.mapInterventionTechnicians(
      interventionTechnicians,
      users
    )
  }

  /**
   * Find technician by email
   * @param email Technician email
   * @param technicians Array of technicians to search
   * @returns Found technician or null
   */
  findTechnicianByEmail(email: string, technicians: any[]): any {
    return TechnicianTools.findByEmail(email, technicians)
  }

  /**
   * Get technician initials
   * @param technician The technician
   * @returns Initials string
   */
  getTechnicianInitials(technician: any): string {
    return TechnicianTools.getInitials(technician)
  }

  /**
   * Get technician color
   * @param technician The technician
   * @returns Color string
   */
  getTechnicianColor(technician: any): string {
    return TechnicianTools.getColor(technician)
  }

  /**
   * Update available technicians list based on selected and users
   * @param users All available users
   * @param selectedTechnicians Currently selected technicians
   * @returns Available technicians (excluding selected)
   */
  getAvailableTechnicians(
    users: UserModel[],
    selectedTechnicians: UserModel[]
  ): UserModel[] {
    return users.filter(
      (user) => !selectedTechnicians.some((selected) => selected.id === user.id)
    )
  }

  /**
   * Build a Map of technician colors by email
   * @param technicians Arrays of technicians
   * @returns Map of email to color
   */
  buildTechnicianColorsMap(...technicianArrays: any[][]): Map<string, string> {
    const colorsMap = new Map<string, string>()

    for (const technicians of technicianArrays) {
      if (technicians) {
        for (const tech of technicians) {
          if (tech.email) {
            colorsMap.set(tech.email, TechnicianTools.getColor(tech))
          }
        }
      }
    }

    return colorsMap
  }

  /**
   * Build a Map of technician initials by email
   * @param technicians Arrays of technicians
   * @returns Map of email to initials
   */
  buildTechnicianInitialsMap(
    ...technicianArrays: any[][]
  ): Map<string, string> {
    const initialsMap = new Map<string, string>()

    for (const technicians of technicianArrays) {
      if (technicians) {
        for (const tech of technicians) {
          if (tech.email) {
            initialsMap.set(tech.email, TechnicianTools.getInitials(tech))
          }
        }
      }
    }

    return initialsMap
  }
}
